import React from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { formatDateAndTime } from "@utils/helpers";
import formatRelativeTime from "./formatRelativeTime";

interface NotificationRowProps {
  id: string | undefined;
  body: string | undefined;
  link: string | undefined;
  readAt: string | undefined | null;
  createdAt: string | undefined;
  onView: (e: React.MouseEvent, id: string | undefined, link: string) => void;
  // "dropdown" (default) preserves the existing NotificationDropdown look.
  // "table" is used inside DataTable, which already supplies its own row height/borders
  variant?: "dropdown" | "table";
}

const NotificationRow: React.FC<NotificationRowProps> = ({
  id,
  body,
  link,
  readAt,
  createdAt,
  onView,
  variant = "dropdown",
}) => {
  const isUnread = !readAt;
  const isTable = variant === "table";

  const isClickable = isTable && !!link;

  return (
    <div
      onClick={isClickable ? (e) => onView(e, id, link as string) : undefined}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        width: isTable ? "100%" : undefined,
        padding: isTable ? "4px 0" : "12px 16px",
        borderBottom: isTable ? undefined : "1px solid #f0f0f0",
        cursor: isClickable ? "pointer" : undefined,
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          backgroundColor: "#418172",
          visibility: isUnread ? "visible" : "hidden",
          flexShrink: 0,
        }}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            justifyContent: "space-between",
            gap: 8,
          }}
        >
          <span
            style={{
              color: "#1c2655",
              fontWeight: isUnread ? 700 : 400,
              fontSize: 14,
            }}
          >
            {body}
          </span>
          {link && (
            <a
              href={link}
              onClick={(e) => {
                if (isClickable) e.stopPropagation();
                onView(e, id, link);
              }}
              style={{
                color: "#418172",
                fontSize: 13,
                whiteSpace: "nowrap",
                flexShrink: 0,
              }}
            >
              View
            </a>
          )}
        </div>
        {createdAt && (
          <OverlayTrigger
            placement="bottom"
            overlay={
              <Tooltip id={`notification-timestamp-tooltip-${id}`}>
                {formatDateAndTime(new Date(createdAt))}
              </Tooltip>
            }
          >
            <div
              style={{
                color: "#6c757d",
                fontSize: 12,
                marginTop: 2,
                width: "fit-content",
              }}
            >
              {formatRelativeTime(createdAt)}
            </div>
          </OverlayTrigger>
        )}
      </div>
    </div>
  );
};

export default NotificationRow;
